import Delta from 'quill-delta';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useLocalize, useViewer } from '@treams/treams-hooks';
import {
	GoalChecklistItemType,
	GoalOwnerType,
	useCreateGoalMutation,
	useMarkTranscriptionFollowupActedUponMutation,
} from '@treams/treams-network';
import { Button, FlexGap, Heading3, Paragraph, tokens } from '@treams/treams-ui';

import type {
	TranscriptionFollowupFeedbackRequestSuggestionData,
	TranscriptionFollowupGoalSuggestionData,
	TranscriptionFollowupsData,
} from './TranscriptionModule';

const Divider = styled.div`
	width: 100%;
	height: 1px;
	background: ${tokens.border.subtle};
`;

const Followup = styled.div<{ $actedUpon?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${tokens.gap.list}px;
	width: 100%;
	box-sizing: border-box;
	padding: ${tokens.padding.compact}px ${tokens.padding.control}px;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.default};
	border-radius: ${tokens.radius.full}px;
	opacity: ${({ $actedUpon }) => ($actedUpon ? 0.6 : 1)};
`;

const FollowupTitle = styled(Paragraph)`
	flex: 1;
	min-width: 0;
	color: ${tokens.text.primary};
`;

type Props = {
	transcriptionID: string;
	followups: TranscriptionFollowupsData;
};

export const TranscriptionFollowups = ({ transcriptionID, followups }: Props) => {
	const { t } = useTranslation();
	const [markActedUpon] = useMarkTranscriptionFollowupActedUponMutation();

	const handleActedUpon = useCallback(
		(followupID: string) => {
			markActedUpon({
				variables: {
					input: {
						transcriptionID,
						followupID,
					},
				},
			});
		},
		[transcriptionID, markActedUpon],
	);

	return (
		<FlexGap mt={`${tokens.gap.group}px`} gap={tokens.gap.list} flexDirection="column">
			<Heading3>{t('transcription.followups')}</Heading3>
			<Divider />
			<FlexGap gap={tokens.gap.element} flexDirection="column">
				{followups.goalSuggestions.map((suggestion) => (
					<GoalSuggestion
						key={suggestion.id}
						suggestion={suggestion}
						onActedUpon={() => handleActedUpon(suggestion.id)}
					/>
				))}
				{followups.feedbackRequestSuggestions.map((suggestion) => (
					<FeedbackRequestSuggestion
						key={suggestion.id}
						suggestion={suggestion}
						onActedUpon={() => handleActedUpon(suggestion.id)}
					/>
				))}
			</FlexGap>
		</FlexGap>
	);
};

const GoalSuggestion = ({
	suggestion,
	onActedUpon,
}: {
	suggestion: TranscriptionFollowupGoalSuggestionData;
	onActedUpon: () => void;
}) => {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const viewer = useViewer();
	const history = useHistory();

	const [createGoal, { loading }] = useCreateGoalMutation();

	const toLocalized = useCallback(<T,>(text: T) => [{ languageID: language.id, text }], [language]);

	const handleClick = useCallback(async () => {
		if (loading) return;

		const result = await createGoal({
			variables: {
				input: {
					title: toLocalized(suggestion.title),
					ownerID: suggestion.targetUser.id,
					description: suggestion.description,
					checklistItems: (suggestion.subgoals ?? []).map((subgoal) => ({
						title: toLocalized(subgoal),
						description: toLocalized(new Delta()),
						progress: {
							type: GoalChecklistItemType.Checkbox,
							value: 0,
						},
					})),
					ownerType: GoalOwnerType.User,
				},
			},
		});

		if (result.data?.createGoal.goal) {
			onActedUpon();

			const goalID = result.data.createGoal.goal.id;
			const isViewerGoal = suggestion.targetUser.id === viewer?.id;

			if (isViewerGoal) {
				history.push(`/goals/${goalID}`);
			} else {
				history.push(`/users/${suggestion.targetUser.id}/goals/${goalID}`);
			}
		}
	}, [loading, createGoal, toLocalized, suggestion, viewer, history, onActedUpon]);

	const targetName =
		suggestion.targetUser.id === viewer?.id
			? t('notes.followups.targetYou')
			: suggestion.targetUser.firstName;

	const title = t('notes.followups.types.goalForTarget', {
		target: targetName,
		goal: suggestion.title,
	});

	return (
		<Followup $actedUpon={suggestion.actedUpon}>
			<FollowupTitle>{title}</FollowupTitle>
			<Button
				title={suggestion.actedUpon ? t('common.again') : t('transcription.followup.createGoal')}
				type="secondary"
				small
				loading={loading}
				onClick={handleClick}
			/>
		</Followup>
	);
};

const FeedbackRequestSuggestion = ({
	suggestion,
	onActedUpon,
}: {
	suggestion: TranscriptionFollowupFeedbackRequestSuggestionData;
	onActedUpon: () => void;
}) => {
	const { t } = useTranslation();
	const history = useHistory();

	const handleClick = useCallback(() => {
		const returnParams = new URLSearchParams();
		returnParams.set('return', window.location.pathname);

		onActedUpon();

		history.push({
			pathname: '/feedback/request-from-suggestion',
			search: `?${returnParams.toString()}`,
			state: { suggestion },
		});
	}, [suggestion, history, onActedUpon]);

	const title = t('notes.followups.types.makeFeedback', { feedback: suggestion.title });

	return (
		<Followup $actedUpon={suggestion.actedUpon}>
			<FollowupTitle>{title}</FollowupTitle>
			<Button
				title={suggestion.actedUpon ? t('common.again') : t('transcription.followup.makeFeedback')}
				type="secondary"
				small
				onClick={handleClick}
			/>
		</Followup>
	);
};
